import { type ReactNode, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { type Role } from "@/lib/api";
import {
  CreditCard,
  LogOut,
  TrendingUp,
  Search,
  ChevronDown,
  Menu,
  HelpCircle,
  Settings,
  Mail,
  LayoutDashboard,
  Users,
  KeyRound,
  SlidersHorizontal,
  FileBarChart,
  History,
  ListChecks,
  ShieldCheck,
  LifeBuoy,
  Lock,
} from "lucide-react";

type NavItem = {
  to: string;
  label: string;
  icon: typeof CreditCard;
  roles: Role[];
};

type NavSection = {
  label: string;
  items: NavItem[];
};

/**
 * Sidebar Liquid — tenant-only.
 *
 * Menus stripés vs socle aviation :
 *  - Refunds : merchant-api n'a pas d'endpoint refund.
 *  - Payouts / Retraits : Liquid = dépôts only.
 *  - Settlements : masqué v1 (backlog si Liquid en a besoin plus tard).
 *  - Webhooks debug : masqué v1 (backlog).
 *  - Notifications : masqué v1 (pas d'endpoint côté merchant-api).
 *  - Renvoyer callback : masqué v1 (pas d'endpoint côté merchant-api).
 *  - Admin sections (Marchands, Équipe Moko, Audit, Switch logs) : Liquid n'accepte pas
 *    de rôle admin (verrouillage sur LIQUID_MERCHANT_CODES côté backend).
 */
const MERCHANT_SECTIONS: NavSection[] = [
  {
    label: "Tableau de bord",
    items: [
      { to: "/merchant/overview", label: "Vue d'ensemble", icon: LayoutDashboard, roles: ["merchant_admin", "finance", "it", "commercial"] },
    ],
  },
  {
    label: "Transactions",
    items: [
      { to: "/payments", label: "Paiements", icon: CreditCard, roles: ["merchant_admin", "finance", "commercial", "it"] },
      { to: "/funnel", label: "Funnel & stats", icon: TrendingUp, roles: ["merchant_admin", "finance", "commercial", "it"] },
      { to: "/transactions/statuses", label: "Statuts", icon: ListChecks, roles: ["merchant_admin", "finance", "it", "commercial"] },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/finance/statements", label: "Statements", icon: FileBarChart, roles: ["merchant_admin", "finance"] },
      { to: "/finance/history", label: "Historique", icon: History, roles: ["merchant_admin", "finance"] },
    ],
  },
  {
    label: "Intégration",
    items: [
      { to: "/merchant/api-keys", label: "Clés API", icon: KeyRound, roles: ["it"] },
    ],
  },
  {
    label: "Mon espace",
    items: [
      { to: "/support", label: "Support", icon: LifeBuoy, roles: ["merchant_admin", "finance", "it", "commercial"] },
      { to: "/merchant/team", label: "Équipe", icon: Users, roles: ["merchant_admin", "finance", "it", "commercial"] },
      { to: "/merchant/roles", label: "Rôles et permissions", icon: ShieldCheck, roles: ["merchant_admin", "finance", "it", "commercial"] },
      { to: "/merchant/settings", label: "Paramètres", icon: SlidersHorizontal, roles: ["merchant_admin"] },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  if (auth.status !== "authenticated") return null;

  const handleLogout = () => {
    auth.logout();
    navigate("/login", { replace: true });
  };

  const filterByRole = (items: NavItem[]) => items.filter((i) => i.roles.includes(auth.user.role));
  const sections = MERCHANT_SECTIONS.map((s) => ({ ...s, items: filterByRole(s.items) })).filter((s) => s.items.length > 0);

  return (
    <div className="min-h-screen flex bg-[#f5f8fa]">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky inset-y-0 left-0 z-40 w-[265px] bg-aside-900 flex flex-col transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ height: "100vh", top: 0 }}
      >
        {/* Logos dual : Liquid × Moko */}
        <div className="h-[60px] px-4 flex items-center justify-center gap-3 border-b border-aside-800 shrink-0">
          <img src="/liquid-logo-white.png" alt="Liquid" className="h-7 w-auto object-contain" />
          <span className="text-white/30 font-light text-lg select-none leading-none" aria-hidden="true">×</span>
          <div className="h-8 rounded-md bg-white/95 px-1.5 flex items-center shrink-0">
            <img src="/moko-logo.png" alt="Moko Afrika" className="h-full w-auto object-contain py-0.5" />
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-4 py-4">
          {sections.map((section) => (
            <div key={section.label}>
              <div className="menu-section-label">{section.label}</div>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `menu-item ${isActive ? "menu-item-active" : ""}`}
                  >
                    <item.icon size={18} />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer — user card */}
        <div className="border-t border-aside-800 p-3 shrink-0">
          <div className="flex items-center gap-3 p-2.5 rounded-lg bg-aside-800/60 hover:bg-aside-800 transition-colors">
            <div className="relative shrink-0">
              <div className="w-10 h-10 rounded-full bg-brand-500 text-white font-bold flex items-center justify-center text-sm">
                {initial(auth.user)}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-success-500 ring-2 ring-aside-900" title="En ligne"></span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-sm font-semibold truncate leading-tight" title={auth.user.email}>
                {auth.user.first_name} {auth.user.last_name}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-brand-500/20 text-brand-300">
                  {auth.user.role}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title="Déconnexion"
              aria-label="Déconnexion"
              className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-aside-text hover:text-white hover:bg-danger-500/20 transition-colors"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <button
          type="button"
          aria-label="Fermer le menu"
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header
          className="h-[60px] sticky top-0 z-20 bg-white flex items-center px-4 lg:px-6 gap-3"
          style={{ boxShadow: "0 1px 0 0 #eff2f5" }}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen((s) => !s)}
            className="lg:hidden p-2 rounded-lg hover:bg-[#f5f8fa]"
            aria-label="Menu"
          >
            <Menu size={20} className="text-[#3f4254]" />
          </button>

          {/* Search — compact */}
          <div className="hidden md:block w-[320px]">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a1a5b7]" />
              <input
                type="text"
                placeholder="Rechercher un paiement, une référence…"
                className="pl-9 pr-3 py-2 w-full bg-[#f5f8fa] rounded-lg text-sm placeholder-[#a1a5b7] focus:outline-none focus:bg-[#eef3f7] transition-colors"
              />
            </div>
          </div>

          {/* Right action bar — pushed to far right */}
          <div className="flex items-center gap-0.5 sm:gap-1 ml-auto">
            <div className="hidden sm:contents">
              <IconButton label="Aide" to="/help">
                <HelpCircle size={18} />
              </IconButton>
            </div>
            <div className="hidden xs:contents">
              <IconButton label="Support" to="/support">
                <Mail size={18} />
              </IconButton>
            </div>
            {auth.user.role === "merchant_admin" && (
              <div className="hidden sm:contents">
                <IconButton label="Paramètres" to="/merchant/settings">
                  <Settings size={18} />
                </IconButton>
              </div>
            )}

            <div className="h-7 w-px bg-[#eff2f5] mx-1 sm:mx-1.5 hidden sm:block" />

            <div className="relative">
              <button
                type="button"
                onClick={() => setProfileOpen((s) => !s)}
                className="flex items-center gap-2 pl-1 pr-1 sm:pr-2 py-1 rounded-lg hover:bg-[#f5f8fa] transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-brand-500 text-white font-bold flex items-center justify-center text-sm shrink-0">
                  {initial(auth.user)}
                </div>
                <div className="hidden lg:block text-left leading-tight">
                  <div className="text-sm font-semibold text-[#181c32]">
                    {auth.user.first_name} {auth.user.last_name}
                  </div>
                  <div className="text-[11px] text-[#7e8299] capitalize">{auth.user.role}</div>
                </div>
                <ChevronDown size={13} className="text-[#7e8299] hidden lg:block" />
              </button>

              {profileOpen && (
                <>
                  <button
                    type="button"
                    aria-label="Fermer"
                    className="fixed inset-0 z-10"
                    onClick={() => setProfileOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-dropdown py-2 z-20 border border-[#eff2f5]">
                    <div className="px-4 py-3 border-b border-[#eff2f5] mb-2 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-brand-500 text-white font-bold flex items-center justify-center">
                        {initial(auth.user)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-[#181c32] text-sm truncate">
                          {auth.user.first_name} {auth.user.last_name}
                        </div>
                        <div className="text-xs text-[#7e8299] truncate" title={auth.user.email}>
                          {auth.user.email}
                        </div>
                        <div className="text-[11px] text-brand-700 uppercase font-bold tracking-wider mt-0.5">{auth.user.role}</div>
                      </div>
                    </div>
                    <Link
                      to="/change-password"
                      onClick={() => setProfileOpen(false)}
                      className="w-full px-4 py-2.5 text-left text-sm text-[#3f4254] hover:bg-[#f5f8fa] flex items-center gap-2.5 font-medium"
                    >
                      <Lock size={15} />
                      Changer le mot de passe
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full px-4 py-2.5 text-left text-sm text-[#3f4254] hover:bg-[#f5f8fa] flex items-center gap-2.5 font-medium"
                    >
                      <LogOut size={15} />
                      Déconnexion
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1">
          <div className="px-4 sm:px-6 lg:px-10 py-5 sm:py-8 max-w-[1400px] mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}

function initial(user: { first_name: string | null; last_name: string | null; email: string }): string {
  const f = user.first_name?.[0] ?? "";
  const l = user.last_name?.[0] ?? "";
  if (f || l) return (f + l).toUpperCase();
  return user.email[0]?.toUpperCase() ?? "?";
}

function IconButton({
  children,
  label,
  badge,
  to,
}: {
  children: ReactNode;
  label: string;
  badge?: number;
  to?: string;
}) {
  const className =
    "relative w-10 h-10 rounded-lg flex items-center justify-center text-[#5e6278] hover:text-brand-700 hover:bg-brand-50 transition-colors";
  const inner = (
    <>
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-danger-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </>
  );
  if (to) {
    return (
      <Link to={to} aria-label={label} title={label} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" aria-label={label} title={label} className={className}>
      {inner}
    </button>
  );
}
